import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { RefreshCw, Zap, Clock, TrendingDown, TrendingUp, Target, Shield, Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  trigger: {
    label: "🟢 TRIGGER",
    color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    rank: 0,
    desc: "Cassure confirmée — entry NOW",
  },
  consolidating: {
    label: "🟡 CONSOLIDATING",
    color: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    rank: 1,
    desc: "Range serré, prêt à casser",
  },
  in_pullback: {
    label: "🟠 IN PULLBACK",
    color: "bg-orange-500/15 text-orange-300 border-orange-500/40",
    rank: 2,
    desc: "Pullback en zone Fibonacci 30-65%",
  },
  waiting_pullback: {
    label: "⏳ WAITING",
    color: "bg-slate-500/15 text-slate-300 border-slate-500/40",
    rank: 3,
    desc: "Spike détecté, pas encore de pullback",
  },
};

const formatRel = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s`;
  if (diff < 3600) return `${Math.round(diff / 60)}m`;
  return `${Math.round(diff / 3600)}h`;
};

export default function IntradayPullback() {
  const [data, setData] = useState({ candidates: [], total: 0, scanned_at: null });
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState("in_pullback");
  const [tick, setTick] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/intraday-pullback/candidates`, {
        params: { min_status: filter },
      });
      setData(res.data || { candidates: [] });
    } catch (e) {
      console.error("intraday pullback fetch", e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/api/intraday-pullback/scan-now`);
      await fetchData();
    } catch (e) {
      console.error("scan-now", e);
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 60s + tick every 5s to update "ago" labels
  useEffect(() => {
    const i1 = setInterval(fetchData, 60_000);
    const i2 = setInterval(() => setTick((t) => t + 1), 5_000);
    return () => {
      clearInterval(i1);
      clearInterval(i2);
    };
  }, [fetchData]);

  const candidates = data.candidates || [];
  const grouped = candidates.reduce((acc, c) => {
    (acc[c.status] = acc[c.status] || []).push(c);
    return acc;
  }, {});

  const orderedStatuses = ["trigger", "consolidating", "in_pullback", "waiting_pullback"];

  return (
    <div className="space-y-6" data-testid="intraday-pullback-tab">
      {/* Header card */}
      <Card className="bg-slate-900/70 border-slate-800">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <TrendingDown className="w-6 h-6 text-emerald-400" />
                Pullback Intraday Live
                <Badge className="ml-2 bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                  no break · no trade
                </Badge>
              </CardTitle>
              <p className="text-sm text-slate-400 mt-2 max-w-2xl">
                Détecte les actions qui ont fait un spike +5 à +25% puis se retracent de 30 à
                65% (Fibonacci). Le signal d'entrée n'est validé que lorsque le prix casse le
                high des 3 dernières bougies 5min — c'est la règle <strong>no break, no trade</strong>.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={triggerScan}
                disabled={scanning}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="pullback-scan-now-button"
              >
                <Zap className={`w-4 h-4 mr-2 ${scanning ? "animate-pulse" : ""}`} />
                {scanning ? "Scan en cours..." : "Scan now"}
              </Button>
              <Button
                onClick={fetchData}
                disabled={loading}
                variant="outline"
                className="border-slate-700"
                data-testid="pullback-refresh-button"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6 flex-wrap text-sm">
            <div className="flex items-center gap-2 text-slate-300">
              <Clock className="w-4 h-4" />
              <span>Dernier scan :</span>
              <strong className="text-white" data-testid="pullback-scanned-at">
                {data.scanned_at ? `il y a ${formatRel(data.scanned_at)}` : "jamais"}
              </strong>
              <span className="text-slate-500 text-xs">(tick {tick})</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <Activity className="w-4 h-4" />
              {data.scanned_universe ?? "?"} tickers scannés ·{" "}
              <strong className="text-white" data-testid="pullback-total-count">
                {data.filtered_total ?? data.total ?? 0}
              </strong>{" "}
              candidats actifs
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-slate-400">Filtre :</span>
              {[
                ["trigger", "🟢 Triggers"],
                ["consolidating", "🟡 + Consolidating"],
                ["in_pullback", "🟠 + Pullback"],
                ["waiting_pullback", "Tous"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    filter === k
                      ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200"
                      : "border-slate-700 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid={`pullback-filter-${k}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Candidates list grouped by status */}
      {candidates.length === 0 ? (
        <Card className="bg-slate-900/40 border-dashed border-slate-700">
          <CardContent className="py-12 text-center text-slate-400">
            <TrendingDown className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-base">Aucun candidat pour le filtre choisi.</p>
            <p className="text-xs mt-1 text-slate-500">
              Le scanner tourne toutes les 15 min pendant les heures US. En dehors, il dort.
            </p>
          </CardContent>
        </Card>
      ) : (
        orderedStatuses.map((s) => {
          const list = grouped[s] || [];
          if (list.length === 0) return null;
          const meta = STATUS_META[s];
          return (
            <div key={s} data-testid={`pullback-group-${s}`}>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-lg font-semibold text-white">
                  {meta.label}{" "}
                  <span className="text-slate-400 font-normal text-sm ml-2">
                    {meta.desc}
                  </span>
                </h3>
                <Badge className={meta.color}>{list.length}</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {list.map((c) => (
                  <PullbackCard key={c.ticker} candidate={c} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function PullbackCard({ candidate: c }) {
  const meta = STATUS_META[c.status] || STATUS_META.waiting_pullback;
  const isActionable = c.entry != null;
  const rrColor =
    c.rr == null
      ? "text-slate-400"
      : c.rr >= 2
      ? "text-emerald-300"
      : c.rr >= 1.5
      ? "text-amber-300"
      : "text-red-300";

  return (
    <Card
      className={`bg-slate-900/70 border-slate-800 hover:border-slate-700 transition`}
      data-testid={`pullback-card-${c.ticker}`}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xl font-bold text-white">{c.ticker}</div>
            <div className="text-xs text-slate-400">
              ${c.last_price} · spike +{c.spike_pct}% · retrace {c.retrace_pct}%
            </div>
          </div>
          <Badge className={meta.color}>{meta.label}</Badge>
        </div>

        {c.ath != null && (
          <div className="flex items-center justify-between bg-gradient-to-r from-purple-500/10 to-transparent border border-purple-500/20 rounded px-2 py-1.5 text-xs">
            <span className="text-slate-400 flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-purple-300" /> ATH
            </span>
            <div className="flex items-center gap-2">
              <span className="text-purple-200 font-semibold">${c.ath}</span>
              {c.pct_from_ath != null && (
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                    c.pct_from_ath >= -5
                      ? "bg-emerald-500/20 text-emerald-200"
                      : c.pct_from_ath >= -20
                      ? "bg-amber-500/20 text-amber-200"
                      : "bg-rose-500/20 text-rose-200"
                  }`}
                  title="Distance du prix live vs ATH"
                >
                  {c.pct_from_ath > 0 ? "+" : ""}{c.pct_from_ath}%
                </span>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-slate-800/40 rounded px-2 py-1.5">
            <div className="text-slate-500">Open</div>
            <div className="text-white font-medium">${c.open}</div>
          </div>
          <div className="bg-slate-800/40 rounded px-2 py-1.5">
            <div className="text-slate-500">High</div>
            <div className="text-emerald-300 font-medium">${c.high_of_day}</div>
          </div>
          <div className="bg-slate-800/40 rounded px-2 py-1.5">
            <div className="text-slate-500">Pullback low</div>
            <div className="text-orange-300 font-medium">${c.pullback_low}</div>
          </div>
        </div>

        {isActionable ? (
          <div className="border-t border-slate-800 pt-3 space-y-2">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-slate-500 flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Entry
                </div>
                <div className="text-emerald-300 font-semibold">${c.entry}</div>
              </div>
              <div>
                <div className="text-slate-500 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Stop
                </div>
                <div className="text-red-300 font-semibold">${c.stop}</div>
              </div>
              <div>
                <div className="text-slate-500 flex items-center gap-1">
                  <Target className="w-3 h-3" /> Target
                </div>
                <div className="text-blue-300 font-semibold">${c.target}</div>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs pt-1">
              <span className="text-slate-400">
                Risk <strong className="text-red-300">{c.risk_pct}%</strong> · Reward{" "}
                <strong className="text-emerald-300">{c.reward_pct}%</strong>
              </span>
              <span className={`font-bold ${rrColor}`}>R:R {c.rr ?? "—"}</span>
            </div>
          </div>
        ) : (
          <div className="border-t border-slate-800 pt-3 text-xs text-slate-500 italic">
            Pas encore d'entry signal — attendre la cassure des 3 dernières bougies 5min.
          </div>
        )}

        <div className="text-[10px] text-slate-600 flex items-center justify-between">
          <span>Vol relatif {c.rel_vol}x</span>
          {c.daily_notional_M && <span>${c.daily_notional_M}M/jour</span>}
        </div>
      </CardContent>
    </Card>
  );
}
